import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useLocation } from 'react-router-dom';
import logoMark from '../../assets/logo-mark.png';
import {
  Package,
  LayoutDashboard,
  PlusCircle,
  Users,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  ChevronRight,
  List,
  MessageSquare,
  CreditCard,
  Shield,
  Send,
  Layout,
  Building2,
  Globe,
  Truck,
  Upload,
  Activity,
  Boxes,
  Network,
  Ship,
  Wallet,
  Plug,
  Store,
} from 'lucide-react';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { PwaInstallBanner } from '../PwaInstallBanner';
import { NotificationHealthBanner } from '../NotificationHealthBanner';
import { GlobalSearch } from '../GlobalSearch';
import { useAuthStore } from '../../store/authStore';
import { useTranslation, useLanguage } from '../../hooks/useTranslation';
import { useInactivityLogout } from '../../hooks/useInactivityLogout';
import { useSSE } from '../../hooks/useSSE';
import { authService } from '../../services/authService';
import { useThemeStore } from '../../store/themeStore';
import { Settings as SettingsIcon, BarChart3, Sun, Moon } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';
import { canAccess } from '../../constants/roleAccess';

interface LayoutProps {
  children: React.ReactNode;
}

interface SubMenuItem {
  label: string;
  path: string;
  icon?: React.ElementType;
}

interface MenuItem {
  icon: React.ElementType;
  label: string;
  path?: string;
  badge?: number;
  subMenu?: SubMenuItem[];
}

const getLogoUrl = (logoUrl: string | null | undefined): string | null => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith('http')) return logoUrl;
  return `${API_BASE_URL}${logoUrl}`;
};

export const MainLayout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, loginNotification, clearLoginNotification, activeTenantId, activeTenantName } = useAuthStore();
  const t = useTranslation();
  const { language } = useLanguage();
  const isEn = language === 'en';
  const { theme, toggleTheme } = useThemeStore();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['colis']);
  const [globalSearchOpen, setGlobalSearchOpen] = useState(false);
  const isSuperAdmin = user?.role === 'super_admin';
  const isLivreur = user?.role === 'livreur';
  const isMultisite = user?.tenant?.type === 'multisite_distribution';
  const isDouane = user?.tenant?.type === 'transit_douane';
  const isEcommerce = user?.tenant?.type === 'ecommerce' || user?.tenant?.type === 'ecommerce_structure';
  const isParticulier = user?.role === 'particulier';
  // Portail vendeur : espace restreint à sa boutique (l'API refuse tout le reste)
  const isVendeur = user?.role === 'vendeur';

  // Auto-logout after 15 minutes of inactivity
  useInactivityLogout();

  // Real-time SSE: invalidate colis cache on status updates
  useSSE(!!user && !isVendeur);

  // Ctrl+K → open global search
  const handleCtrlK = useCallback((e: KeyboardEvent) => {
    if (!canAccess(user?.role, '/colis')) return;
    if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
      e.preventDefault();
      setGlobalSearchOpen((v) => !v);
    }
  }, [user?.role]);

  // Stable onClose — without useCallback this would be a new function ref on every
  // MainLayout render, causing GlobalSearch keyboard useEffect to re-register listeners
  const handleSearchClose = useCallback(() => setGlobalSearchOpen(false), []);

  useEffect(() => {
    document.addEventListener('keydown', handleCtrlK);
    return () => document.removeEventListener('keydown', handleCtrlK);
  }, [handleCtrlK]);

  // Show login notification once on first render after login
  useEffect(() => {
    if (!loginNotification) return;
    const { lastLoginAt, failedAttempts } = loginNotification;

    const dateStr = lastLoginAt
      ? new Date(lastLoginAt).toLocaleString()
      : null;

    const loginLine = dateStr
      ? `${t.common.lastConnection}: ${dateStr}`
      : t.common.firstConnection;

    const attemptsLine =
      failedAttempts > 0
        ? `⚠ ${failedAttempts} ${t.common.failedAttempts}`
        : `✓ ${t.common.noFailedAttempts}`;

    toast.custom(
      (toastInst) => (
        <div
          className={`${
            toastInst.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
          } transition-all duration-300 max-w-sm w-full bg-slate-800/95 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl p-4`}
        >
          <p className="text-white font-semibold text-sm mb-1">{loginLine}</p>
          <p className={`text-xs ${failedAttempts > 0 ? 'text-yellow-300' : 'text-green-400'}`}>
            {attemptsLine}
          </p>
        </div>
      ),
      { duration: 7000, position: 'bottom-right' }
    );

    clearLoginNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logoUrl = getLogoUrl(user?.tenant?.logo_url);

  const fullMenuItems: MenuItem[] = [
    {
      icon: LayoutDashboard,
      label: t.common.dashboard,
      path: '/dashboard',
    },
    // Livreur app shortcut — visible only for livreurs
    ...(isLivreur
      ? [
          {
            icon: Truck,
            label: t.livreurApp.title,
            path: '/livreur-app',
          },
        ]
      : []),
    // Module Particulier/livreur dédié — raccourci visible seulement pour
    // les clients particuliers (même schéma que isLivreur ci-dessus).
    ...(isParticulier
      ? [
          {
            icon: Package,
            label: language === 'fr' ? 'Mes envois' : 'My shipments',
            path: '/mes-envois',
          },
        ]
      : []),
    {
      icon: Package,
      label: t.common.parcels,
      subMenu: [
        {
          label: t.common.listeColis,
          path: '/colis',
          icon: List,
        },
        // Point d'entrée unique : /create-colis affiche le formulaire adapté à la
        // typologie (national, cargo, e-commerce…) — voir DEDICATED_FORM_ROUTES.
        {
          label: t.common.createParcel,
          path: '/create-colis',
          icon: PlusCircle,
        },
        {
          label: language === 'fr' ? 'Import en masse' : 'Bulk Import',
          path: '/colis/import',
          icon: Upload,
        },
        {
          label: language === 'fr' ? 'Expéditions' : 'Shipments',
          path: '/expeditions',
          icon: Boxes,
        },
      ],
    },
    {
      icon: Users,
      label: t.livreurs.title,
      path: '/livreurs',
    },
    {
      icon: Wallet,
      label: language === 'fr' ? 'Réconciliation' : 'Reconciliation',
      path: '/reconciliation',
    },
    {
      icon: FileText,
      label: t.factures.title,
      path: '/factures',
    },
    {
      icon: MessageSquare,
      label: t.common.notifications,
      subMenu: [
        { label: t.common.notifHistory, path: '/notifications', icon: Send },
        { label: t.common.notifTemplates, path: '/notifications/templates', icon: Layout },
        { label: t.common.wallet, path: '/wallet', icon: CreditCard },
      ],
    },
    {
      icon: BarChart3,
      label: t.common.reports,
      path: '/reports',
    },
    // Module Multisites/Hubs/Distribution — visible seulement pour les
    // tenants de typologie MULTISITE_DISTRIBUTION.
    ...(isMultisite
      ? [
          {
            icon: Network,
            label: language === 'fr' ? 'Réseau' : 'Network',
            subMenu: [
              { label: language === 'fr' ? 'Vue d\'ensemble' : 'Overview', path: '/reseau', icon: Network },
              { label: language === 'fr' ? 'Sites' : 'Sites', path: '/reseau/sites', icon: Building2 },
              { label: language === 'fr' ? 'Manifestes' : 'Manifests', path: '/reseau/manifestes', icon: Truck },
              { label: language === 'fr' ? 'Tournées' : 'Tours', path: '/reseau/tournees', icon: List },
              { label: language === 'fr' ? 'Compensation' : 'Compensation', path: '/reseau/compensation', icon: CreditCard },
            ],
          },
        ]
      : []),
    // Module Transit/Dédouanement — visible seulement pour les tenants de
    // typologie TRANSIT_DOUANE.
    ...(isDouane
      ? [
          {
            icon: Ship,
            label: language === 'fr' ? 'Transit & Douane' : 'Transit & Customs',
            path: '/transit',
          },
        ]
      : []),
    // Module E-Commerce & Suivi de Livraison — visible seulement pour les
    // tenants ecommerce / ecommerce_structure.
    ...(isEcommerce
      ? [
          {
            icon: Package,
            label: language === 'fr' ? 'E-commerce' : 'E-commerce',
            subMenu: [
              { label: language === 'fr' ? 'Vendeurs' : 'Sellers', path: '/merchants', icon: Users },
              { label: language === 'fr' ? 'Intégrations API' : 'API integrations', path: '/integrations', icon: Plug },
            ],
          },
        ]
      : []),
    {
      icon: SettingsIcon,
      label: t.common.settings,
      path: '/settings',
    },
    ...(isSuperAdmin
      ? [
          {
            icon: Shield,
            label: 'Super Admin',
            subMenu: [
              { label: 'Configuration', path: '/super-admin/config', icon: SettingsIcon },
              { label: 'Quotas SMS/WhatsApp', path: '/super-admin/quota', icon: CreditCard },
              { label: 'Monitoring système', path: '/super-admin/system', icon: Activity },
            ],
          },
        ]
      : []),
  ];

  // Un compte Vendeur n'a qu'une entrée : son espace (l'API refuse le reste).
  // Menu limité aux pages que le rôle peut réellement ouvrir (voir roleAccess.ts)
  const role = user?.role;
  const visible = (p?: string) => !p || canAccess(role, p);
  const menuItems: MenuItem[] = isVendeur
    ? [{ icon: Store, label: language === 'fr' ? 'Mon espace vendeur' : 'My seller space', path: '/vendeur' }]
    : fullMenuItems
        .map((item) =>
          item.subMenu
            ? { ...item, subMenu: item.subMenu.filter((s) => visible(s.path)) }
            : item,
        )
        .filter((item) => (item.subMenu ? item.subMenu.length > 0 : visible(item.path)));

  const handleLogout = async () => {
    await authService.logout(); // révoque le refresh token côté serveur
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => location.pathname === path;

  const isMenuExpanded = (label: string) => expandedMenus.includes(label);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label]
    );
  };

  const isSubMenuActive = (subMenu?: SubMenuItem[]) => {
    if (!subMenu) return false;
    return subMenu.some((item) => location.pathname === item.path);
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasSubMenu = item.subMenu && item.subMenu.length > 0;
    const isExpanded = isMenuExpanded(item.label);
    const isActiveMenu = item.path ? isActive(item.path) : isSubMenuActive(item.subMenu);

    return (
      <div key={item.label}>
        <button
          onClick={() => {
            if (hasSubMenu) {
              toggleMenu(item.label);
            } else if (item.path) {
              navigate(item.path);
            }
          }}
          className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
            isActiveMenu
              ? 'bg-white/20 text-white shadow-lg'
              : 'text-white/70 hover:bg-white/10 hover:text-white'
          }`}
        >
          <item.icon className="w-5 h-5 flex-shrink-0" />
          {sidebarOpen && (
            <>
              <span className="flex-1 text-left">{item.label}</span>
              {item.badge && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                  {item.badge}
                </span>
              )}
              {hasSubMenu && (
                <ChevronRight
                  className={`w-4 h-4 transition-transform ${
                    isExpanded ? 'rotate-90' : ''
                  }`}
                />
              )}
            </>
          )}
        </button>

        {hasSubMenu && sidebarOpen && isExpanded && (
          <div className="ml-4 mt-2 space-y-1 border-l-2 border-white/20 pl-4">
            {item.subMenu!.map((subItem) => (
              <button
                key={subItem.path}
                onClick={() => navigate(subItem.path)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                  isActive(subItem.path)
                    ? 'bg-white/15 text-white font-medium'
                    : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
              >
                {subItem.icon && <subItem.icon className="w-4 h-4" />}
                <span>{subItem.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  const SidebarHeader = ({ showClose = false }: { showClose?: boolean }) => (
    <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
      <div className="flex items-center gap-3">
        {/* Logo or icon */}
        <div
          className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
          style={{ background: 'linear-gradient(to bottom right, var(--color-primary), var(--color-secondary))' }}
        >
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={user?.tenant?.nom || 'Logo'}
              className="w-full h-full object-cover"
            />
          ) : (
            <img src={logoMark} alt="CeDo Trace" className="w-7 h-7 object-contain" />
          )}
        </div>
        {sidebarOpen && (
          <div>
            <h1 className="text-white font-bold text-lg leading-tight">
              {user?.tenant?.nom || 'CEDO Trace'}
            </h1>
          </div>
        )}
      </div>
      {showClose && (
        <button
          onClick={() => setMobileMenuOpen(false)}
          className="text-white/70 hover:text-white"
        >
          <X className="w-6 h-6" />
        </button>
      )}
    </div>
  );

  return (
    <div
      className={`min-h-screen ${theme === 'light' ? 'bg-gradient-to-br from-slate-50 via-purple-50 to-slate-50' : ''}`}
      style={theme === 'dark' ? {
        backgroundImage: 'linear-gradient(to bottom right, #0f172a, color-mix(in srgb, var(--color-primary) 30%, #0f172a) 50%, #0f172a 100%)',
      } : undefined}
    >
      {/* Background Pattern */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS1vcGFjaXR5PSIwLjAzIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-40"></div>

      {/* Sidebar Desktop */}
      <aside
        className={`fixed left-0 top-0 h-full bg-white/5 backdrop-blur-2xl border-r border-white/10 transition-all duration-300 z-40 ${
          sidebarOpen ? 'w-64' : 'w-20'
        } hidden lg:block overflow-y-auto`}
      >
        <SidebarHeader />

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => renderMenuItem(item))}
        </nav>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 w-6 h-6 bg-white/20 backdrop-blur-md border border-white/30 rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-all"
        >
          {sidebarOpen ? '←' : '→'}
        </button>
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          <aside className="absolute left-0 top-0 h-full w-64 bg-white/5 backdrop-blur-2xl border-r border-white/10 overflow-y-auto">
            <SidebarHeader showClose />
            <nav className="p-4 space-y-2">
              {menuItems.map((item) => {
                const hasSubMenu = item.subMenu && item.subMenu.length > 0;
                const isExpanded = isMenuExpanded(item.label);
                const isActiveMenu = item.path ? isActive(item.path) : isSubMenuActive(item.subMenu);

                return (
                  <div key={item.label}>
                    <button
                      onClick={() => {
                        if (hasSubMenu) {
                          toggleMenu(item.label);
                        } else if (item.path) {
                          navigate(item.path);
                          setMobileMenuOpen(false);
                        }
                      }}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActiveMenu
                          ? 'bg-white/20 text-white'
                          : 'text-white/70 hover:bg-white/10 hover:text-white'
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      <span className="flex-1 text-left">{item.label}</span>
                      {hasSubMenu && (
                        <ChevronRight
                          className={`w-4 h-4 transition-transform ${
                            isExpanded ? 'rotate-90' : ''
                          }`}
                        />
                      )}
                    </button>

                    {hasSubMenu && isExpanded && (
                      <div className="ml-4 mt-2 space-y-1 border-l-2 border-white/20 pl-4">
                        {item.subMenu!.map((subItem) => (
                          <button
                            key={subItem.path}
                            onClick={() => {
                              navigate(subItem.path);
                              setMobileMenuOpen(false);
                            }}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all ${
                              isActive(subItem.path)
                                ? 'bg-white/15 text-white font-medium'
                                : 'text-white/60 hover:bg-white/10 hover:text-white'
                            }`}
                          >
                            {subItem.icon && <subItem.icon className="w-4 h-4" />}
                            <span>{subItem.label}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        }`}
      >
        {/* Header */}
        <header className="h-16 bg-white/5 backdrop-blur-2xl border-b border-white/10 sticky top-0 z-30">
          <div className="h-full px-4 flex items-center justify-between">
            {/* Left */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden text-white"
              >
                <Menu className="w-6 h-6" />
              </button>

              {canAccess(role, '/colis') && (
              <button
                onClick={() => setGlobalSearchOpen(true)}
                className="hidden md:flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-4 py-2 w-72 hover:bg-white/15 transition-all group"
              >
                <Search className="w-4 h-4 text-white/60" />
                <span className="text-white/40 text-sm flex-1 text-left">
                  {isEn ? 'Search...' : 'Rechercher...'}
                </span>
                <kbd className="flex items-center gap-0.5 px-1.5 py-0.5 bg-white/10 border border-white/20 rounded text-white/30 text-xs font-mono group-hover:text-white/50 transition-colors">
                  Ctrl K
                </kbd>
              </button>
              )}
            </div>

            {/* Right */}
            <div className="flex items-center gap-4">
              <LanguageSwitcher />

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
                title={theme === 'dark' ? 'Mode clair' : 'Mode sombre'}
              >
                {theme === 'dark' ? (
                  <Sun className="w-5 h-5 text-yellow-300" />
                ) : (
                  <Moon className="w-5 h-5 text-slate-600" />
                )}
              </button>

              {canAccess(role, '/notifications') && (
              <button
                onClick={() => navigate('/notifications')}
                className="relative p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
                title={t.common.notifications}
              >
                <Bell className="w-5 h-5 text-white" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
              )}

              {/* Profile Menu */}
              <div className="relative">
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition-all"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-sm"
                    style={{ background: 'linear-gradient(to bottom right, var(--color-primary), var(--color-secondary))' }}
                  >
                    {user?.nom?.charAt(0).toUpperCase()}
                  </div>
                  <div className="hidden sm:block text-left">
                    <p className="text-white text-sm font-medium">{user?.nom}</p>
                    <p className="text-white/60 text-xs">{user?.role}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-white/60" />
                </button>

                {profileMenuOpen && (
                  <div className="absolute right-0 top-14 w-52 bg-white/10 backdrop-blur-2xl border border-white/20 rounded-xl shadow-2xl overflow-hidden">
                    <button
                      onClick={() => {
                        navigate('/profile');
                        setProfileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-white/90 hover:bg-white/10 transition-all"
                    >
                      <Users className="w-4 h-4" />
                      <span className="text-sm">{t.common.myProfile}</span>
                    </button>
                    <button
                      onClick={() => {
                        navigate('/settings');
                        setProfileMenuOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-white/90 hover:bg-white/10 transition-all"
                    >
                      <Settings className="w-4 h-4" />
                      <span className="text-sm">{t.common.settings}</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-white/10 transition-all border-t border-white/10"
                    >
                      <LogOut className="w-4 h-4" />
                      <span className="text-sm">{t.common.logout}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Super Admin tenant context banner */}
        {isSuperAdmin && activeTenantId && (
          <div className={`px-4 py-2 flex items-center justify-between text-sm border-b ${
            activeTenantId === '__all__'
              ? 'bg-violet-900/30 border-violet-500/20 text-violet-200'
              : 'bg-indigo-900/30 border-indigo-500/20 text-indigo-200'
          }`}>
            <div className="flex items-center gap-2">
              {activeTenantId === '__all__'
                ? <Globe className="w-3.5 h-3.5 opacity-70" />
                : <Building2 className="w-3.5 h-3.5 opacity-70" />}
              <span className="opacity-70">
                {activeTenantId === '__all__'
                  ? (isEn ? 'Global view — all tenants' : 'Vue globale — tous les tenants')
                  : (isEn ? 'Operating as:' : 'Opérant en tant que :')}
              </span>
              {activeTenantId !== '__all__' && (
                <span className="font-semibold">{activeTenantName}</span>
              )}
            </div>
            <button
              onClick={() => navigate('/select-tenant')}
              className="flex items-center gap-1 opacity-70 hover:opacity-100 transition-opacity text-xs"
            >
              {isEn ? 'Switch' : 'Changer'}
              <ChevronRight className="w-3 h-3" />
            </button>
          </div>
        )}

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8 relative z-10">
          <NotificationHealthBanner />
          {children}
        </main>
      </div>

      {/* Global Search (Ctrl+K) */}
      <GlobalSearch open={globalSearchOpen} onClose={handleSearchClose} />

      {/* PWA Install Banner */}
      <PwaInstallBanner />
    </div>
  );
};
